import type { ChatMessageCode, VyroChatMessage } from './types'
import { MESSAGE_SOURCE } from './types'

/**
 * Parse and validate an inbound postMessage from the iframe.
 *
 * Rejects (returns null) anything not from the expected iframe origin, not JSON,
 * or not a well-formed Vyro chat message. Never throws — a hostile page may post
 * arbitrary junk to `window`, and the forms loader's original `throw` on unknown
 * codes is deliberately not reproduced here.
 */
export function parseMessage(
  event: MessageEvent,
  expectedOrigin: string,
): VyroChatMessage | null {
  if (event.origin !== expectedOrigin) return null

  let data: unknown
  if (typeof event.data === 'string') {
    try {
      data = JSON.parse(event.data)
    } catch {
      return null
    }
  } else {
    data = event.data
  }

  if (!data || typeof data !== 'object') return null
  const message = data as Record<string, unknown>
  if (message.source !== MESSAGE_SOURCE) return null
  if (typeof message.code !== 'string') return null
  if (!message.code.startsWith('vyro:chat:')) return null

  return message as unknown as VyroChatMessage
}

/** Post a message down into the iframe, targeting its known origin. */
export function postToIframe(
  iframe: HTMLIFrameElement,
  targetOrigin: string,
  code: ChatMessageCode,
  payload: unknown,
): void {
  const frameWindow = iframe.contentWindow
  if (!frameWindow) return
  const message: VyroChatMessage = { source: MESSAGE_SOURCE, code, payload }
  frameWindow.postMessage(JSON.stringify(message), targetOrigin)
}
